import React, { useMemo } from "react";
import { Box, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody, TextField } from "@mui/material";
import InvoiceTotal from "./InvoiceTotal";

const money2 = (v) => {
  const x = Number(String(v ?? "").replace(",", "."));
  if (!Number.isFinite(x)) return "0.00";
  return x.toFixed(2);
};

const num0 = (v) => {
  const x = Number(String(v ?? "").replace(",", "."));
  return Number.isFinite(x) ? x : 0;
};

const disabledFieldSx = {
  "& .Mui-disabled": {
    backgroundColor: "#f0f0f0 !important",
    color: "#333 !important",
    WebkitTextFillColor: "#333 !important",
  },
};

const deepFindArray = (obj, key, depth = 6) => {
  if (!obj || typeof obj !== "object" || depth <= 0) return [];
  if (Array.isArray(obj[key])) return obj[key];
  for (const k of Object.keys(obj)) {
    const v = obj[k];
    if (v && typeof v === "object") {
      const r = deepFindArray(v, key, depth - 1);
      if (Array.isArray(r) && r.length) return r;
    }
  }
  return [];
};

const isCuentaRow = (d) => {
  if (!d || typeof d !== "object") return false;
  if (String(d.tipo_item || "").toUpperCase() === "CUENTA") return true;
  if (d.es_cuenta === true) return true;
  if (d.cuenta && typeof d.cuenta === "object") return true;
  if (typeof d.cuenta === "string" && d.cuenta.trim() !== "") return true;
  if (d.cuenta_id || d.cuenta_codigo || d.cuenta_nombre) return true;
  return false;
};

const cellSx = { py: 0.6, px: 1, verticalAlign: "middle" };
const headCellSx = { ...cellSx, color: "#fff", whiteSpace: "nowrap", fontWeight: 700 };
const nowrap = { whiteSpace: "nowrap" };

export default function InvoiceAccount({ doc }) {
  const origen = String(doc?.origen || "").toUpperCase();

  const detallesAll = useMemo(() => {
    const a = Array.isArray(doc?.detalles) ? doc.detalles : deepFindArray(doc, "detalles");
    return Array.isArray(a) ? a : [];
  }, [doc]);

  const cuentas = useMemo(() => {
    if (origen !== "COMPRA") return [];
    return detallesAll.filter(isCuentaRow);
  }, [detallesAll, origen]);

  if (origen !== "COMPRA") {
    return (
      <Box>
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <Typography sx={{ fontWeight: 700 }}>Este documento no es una compra.</Typography>
        </Paper>
      </Box>
    );
  }

  if (!cuentas.length) {
    return (
      <Box>
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <Typography sx={{ fontWeight: 700 }}>No hay cuentas registradas.</Typography>
        </Paper>
        <InvoiceTotal doc={doc} readOnly />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <Table size="small" sx={{ tableLayout: "auto" }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#1565C0" }}>
              <TableCell sx={headCellSx}>Cant.</TableCell>
              <TableCell sx={headCellSx}>Cuenta</TableCell>
              <TableCell sx={headCellSx} align="right">Valor U</TableCell>
              <TableCell sx={headCellSx} align="right">IVA %</TableCell>
              <TableCell sx={headCellSx} align="right">IVA $</TableCell>
              <TableCell sx={headCellSx} align="right">ICE %</TableCell>
              <TableCell sx={headCellSx} align="right">ICE $</TableCell>
              <TableCell sx={headCellSx} align="right">Desc. %</TableCell>
              <TableCell sx={headCellSx} align="right">Desc. $</TableCell>
              <TableCell sx={headCellSx} align="right">Subtotal</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {cuentas.map((d, i) => {
              const qty = num0(d.cantidad);
              const unit = num0(d.valor_unitario ?? d.costo_unitario ?? d.precio_unitario);

              const descPct = num0(d.descuento_pct);
              const descVal = num0(d.descuento_valor);

              const ivaPct = num0(d.iva_porcentaje);
              const ivaVal = num0(d.iva_valor);

              const icePct = num0(d.ice_porcentaje);
              const iceVal = num0(d.ice_valor);

              const baseFromBackend = num0(d.base_imponible);
              const baseFallback = Math.max(0, qty * unit - descVal);
              const subtotal = baseFromBackend > 0 ? baseFromBackend : baseFallback;

              const cuentaObj = d.cuenta && typeof d.cuenta === "object" ? d.cuenta : null;
              const cuentaCodigo =
                (cuentaObj?.codigo || d.cuenta_codigo || d.cuenta || "").toString().trim() ||
                (d.codigo || "").toString().trim();
              const cuentaNombre = (cuentaObj?.nombre || d.cuenta_nombre || "").toString().trim();
              const cuentaLabel = cuentaNombre ? `${cuentaCodigo} - ${cuentaNombre}` : `${cuentaCodigo}`;

              return (
                <TableRow key={i} hover>
                  <TableCell sx={cellSx}>
                    <TextField
                      value={String(qty || "")}
                      disabled
                      size="small"
                      inputProps={{ readOnly: true, style: { textAlign: "right" } }}
                      sx={{ width: 60, ...disabledFieldSx }}
                    />
                  </TableCell>

                  <TableCell sx={{ ...cellSx, minWidth: 260 }}>
                    <Typography sx={{ fontWeight: 800, color: "#111827" }}>{cuentaLabel}</Typography>
                  </TableCell>

                  <TableCell sx={{ ...cellSx, ...nowrap }} align="right">
                    <TextField
                      value={`$${money2(unit)}`}
                      disabled
                      size="small"
                      inputProps={{ readOnly: true, style: { textAlign: "right" } }}
                      sx={{ width: 90, ...disabledFieldSx }}
                    />
                  </TableCell>

                  <TableCell sx={{ ...cellSx, ...nowrap }} align="right">
                    <TextField
                      value={money2(ivaPct)}
                      disabled
                      size="small"
                      inputProps={{ readOnly: true, style: { textAlign: "right" } }}
                      sx={{ width: 70, ...disabledFieldSx }}
                    />
                  </TableCell>

                  <TableCell sx={{ ...cellSx, ...nowrap }} align="right">
                    <TextField
                      value={`$${money2(ivaVal)}`}
                      disabled
                      size="small"
                      inputProps={{ readOnly: true, style: { textAlign: "right" } }}
                      sx={{ width: 90, ...disabledFieldSx }}
                    />
                  </TableCell>

                  <TableCell sx={{ ...cellSx, ...nowrap }} align="right">
                    <TextField
                      value={money2(icePct)}
                      disabled
                      size="small"
                      inputProps={{ readOnly: true, style: { textAlign: "right" } }}
                      sx={{ width: 70, ...disabledFieldSx }}
                    />
                  </TableCell>

                  <TableCell sx={{ ...cellSx, ...nowrap }} align="right">
                    <TextField
                      value={`$${money2(iceVal)}`}
                      disabled
                      size="small"
                      inputProps={{ readOnly: true, style: { textAlign: "right" } }}
                      sx={{ width: 90, ...disabledFieldSx }}
                    />
                  </TableCell>

                  <TableCell sx={{ ...cellSx, ...nowrap }} align="right">
                    <TextField
                      value={money2(descPct)}
                      disabled
                      size="small"
                      inputProps={{ readOnly: true, style: { textAlign: "right" } }}
                      sx={{ width: 70, ...disabledFieldSx }}
                    />
                  </TableCell>

                  <TableCell sx={{ ...cellSx, ...nowrap }} align="right">
                    <TextField
                      value={`$${money2(descVal)}`}
                      disabled
                      size="small"
                      inputProps={{ readOnly: true, style: { textAlign: "right" } }}
                      sx={{ width: 90, ...disabledFieldSx }}
                    />
                  </TableCell>

                  <TableCell sx={{ ...cellSx, ...nowrap }} align="right">
                    <TextField
                      value={`$${money2(subtotal)}`}
                      disabled
                      size="small"
                      inputProps={{ readOnly: true, style: { textAlign: "right" } }}
                      sx={{ width: 120, ...disabledFieldSx }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      <InvoiceTotal doc={doc} readOnly />
    </Box>
  );
}
